import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Chip,
} from '@mui/material';
import { useUpdateUser, useAppDbUserSettings } from '@/lib/query/hooks/useUsers';
import { useCompanyAccounts } from '@/lib/query/hooks/useCompanies';
import {
  useMiseSellersCurrent,
  useMiseSellerGroupsCurrent,
} from '@/lib/query/hooks/useMiseSellers';
import { useMiseSellers, useMiseSellerGroups } from '@/lib/query/hooks/useMiseAccounts';
import { indentSellerGroupLabel, sortSellerGroupsByTree } from '@/lib/mise/sellerGroupTree';
import { buildSellerTreeOptions } from '@/lib/mise/sellerTree';
import { SellerTreeOptionList } from '@/components/mise/SellerTreeOptionList';
import { EXCLUDED_SELLER_ID } from '@/lib/types/mise/seller';
import type { CompanyUser } from '@/lib/types/mise/company';
import type { UpdateUserRequest } from '@/lib/types/mise/user';

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  user: CompanyUser;
}

export function EditUserDialog({ open, onClose, companyId, user }: EditUserDialogProps) {
  const [userName, setUserName] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [defaultAccountId, setDefaultAccountId] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [comparisonType, setComparisonType] = useState<'seller' | 'seller_group'>('seller');
  const [comparisonSellerId, setComparisonSellerId] = useState('');
  const [comparisonSellerGroupId, setComparisonSellerGroupId] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateUserMutation = useUpdateUser(companyId);
  const { data: accounts = [], isLoading: accountsLoading } = useCompanyAccounts(companyId);

  // appdbのユーザー設定を取得（比較店舗の初期値用）
  const { data: appDbSettings, isLoading: appDbSettingsLoading } = useAppDbUserSettings(
    companyId,
    user.user_id,
    user.default_account_id || '',
  );

  // 店舗一覧の取得（sellers_current優先、snapshot_hot.sellersフォールバック）
  const { data: sellersCurrent = [], isLoading: sellersCurrentLoading } =
    useMiseSellersCurrent(defaultAccountId);
  const { data: sellersSnapshot = [], isLoading: sellersSnapshotLoading } =
    useMiseSellers(defaultAccountId);

  const sellers = useMemo(() => {
    const source = sellersCurrent.length > 0 ? sellersCurrent : sellersSnapshot;
    return source.filter((s) => s.id !== EXCLUDED_SELLER_ID);
  }, [sellersCurrent, sellersSnapshot]);

  // 店舗・店舗グループを階層表示するための階層データ（snapshot_hot 由来）
  const { data: sellerGroupNodes = [], isLoading: sellerGroupNodesLoading } =
    useMiseSellerGroups(defaultAccountId);

  const sellersLoading = defaultAccountId
    ? sellersCurrentLoading || sellersSnapshotLoading || sellerGroupNodesLoading
    : false;

  // 店舗を所属する店舗グループの階層の下に並べる（所属は snapshot_hot.sellers から引く）
  const sellerTreeOptions = useMemo(
    () => buildSellerTreeOptions(sellers, sellerGroupNodes, sellersSnapshot),
    [sellers, sellerGroupNodes, sellersSnapshot],
  );

  // 店舗グループ一覧の取得
  const { data: sellerGroups = [], isLoading: sellerGroupsCurrentLoading } =
    useMiseSellerGroupsCurrent(defaultAccountId);
  const sellerGroupsLoading = sellerGroupsCurrentLoading || sellerGroupNodesLoading;

  // 同名の店舗グループを見分けられるよう、階層順に並べて字下げする
  const sellerGroupOptions = useMemo(
    () => sortSellerGroupsByTree(sellerGroups, sellerGroupNodes),
    [sellerGroups, sellerGroupNodes],
  );

  // 初期値の設定
  useEffect(() => {
    if (user && open) {
      setUserName(user.user_name || '');
      setSelectedAccountIds([...user.account_ids]);
      setDefaultAccountId(user.default_account_id || '');
      setSellerId('');
      setComparisonType('seller');
      setComparisonSellerId('');
      setComparisonSellerGroupId('');
      setErrors({});
      updateUserMutation.reset();
    }
  }, [user, open]);

  // appdb設定が取得できたら比較店舗の初期値を設定
  useEffect(() => {
    if (appDbSettings && open) {
      if (appDbSettings.comparison_seller_group_id) {
        setComparisonType('seller_group');
        setComparisonSellerGroupId(appDbSettings.comparison_seller_group_id);
        setComparisonSellerId('');
      } else if (appDbSettings.comparison_seller_id) {
        setComparisonType('seller');
        setComparisonSellerId(appDbSettings.comparison_seller_id);
        setComparisonSellerGroupId('');
      } else {
        setComparisonType('seller');
        setComparisonSellerId('');
        setComparisonSellerGroupId('');
      }
    }
  }, [appDbSettings, open]);

  // バリデーション
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!userName.trim()) {
      newErrors.userName = 'ユーザー名は必須です';
    }

    if (selectedAccountIds.length === 0) {
      newErrors.accountIds = '少なくとも1つのアカウントを選択してください';
    }

    if (!defaultAccountId) {
      newErrors.defaultAccountId = 'デフォルトアカウントは必須です';
    } else if (!selectedAccountIds.includes(defaultAccountId)) {
      newErrors.defaultAccountId =
        'デフォルトアカウントはアクセス可能なアカウントから選択してください';
    }

    if (comparisonType === 'seller' && !comparisonSellerId) {
      newErrors.comparisonSeller = '比較店舗は必須です';
    }
    if (comparisonType === 'seller_group' && !comparisonSellerGroupId) {
      newErrors.comparisonSellerGroup = '比較店舗グループは必須です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (updateUserMutation.isPending) return;
    setConfirmDialogOpen(false);
    onClose();
  };

  const handleUpdateClick = () => {
    if (!validate()) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!validate()) {
      setConfirmDialogOpen(false);
      return;
    }

    const request: UpdateUserRequest = {
      user_name: userName.trim(),
      account_ids: selectedAccountIds,
      default_account_id: defaultAccountId,
      seller_id: sellerId || undefined,
      comparison_seller_id: comparisonType === 'seller' ? comparisonSellerId : undefined,
      comparison_seller_group_id:
        comparisonType === 'seller_group' ? comparisonSellerGroupId : undefined,
    };

    try {
      await updateUserMutation.mutateAsync({ userId: user.user_id, request });
      handleClose();
    } catch (error) {
      console.error('ユーザー更新に失敗しました:', error);
    }
  };

  const handleAccountToggle = (accountId: string) => {
    setSelectedAccountIds((prev) => {
      const next = prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId];
      // デフォルトアカウントが外された場合はクリア
      if (!next.includes(defaultAccountId)) {
        setDefaultAccountId('');
        setSellerId('');
      }
      return next;
    });
  };

  // 選択されたアカウントの表示名を取得（確認ダイアログ用）
  const getAccountDisplayName = (accountId: string) => {
    const account = accounts.find((acc) => acc.id === accountId);
    if (!account) return accountId;
    return account.name || account.id;
  };

  // 選択された店舗の情報を取得
  const selectedSeller = sellers.find((s) => s.id === sellerId);
  // 選択された比較店舗/比較店舗グループの情報を取得
  const selectedComparisonSeller = sellers.find((s) => s.id === comparisonSellerId);
  const selectedComparisonSellerGroup = sellerGroups.find((g) => g.id === comparisonSellerGroupId);

  return (
    <>
      {/* 編集ダイアログ */}
      <Dialog open={open && !confirmDialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザーを編集</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {updateUserMutation.isError && (
              <Alert severity="error">
                {updateUserMutation.error instanceof Error
                  ? updateUserMutation.error.message
                  : 'ユーザーの更新に失敗しました。'}
              </Alert>
            )}

            {/* メールアドレス（読み取り専用） */}
            <TextField
              label="メールアドレス"
              value={user.user_email}
              disabled
              fullWidth
              helperText="メールアドレスは変更できません"
            />

            {/* ユーザー名 */}
            <TextField
              label="ユーザー名"
              value={userName}
              onChange={(e) => {
                setUserName(e.target.value);
                if (errors.userName) {
                  setErrors({ ...errors, userName: '' });
                }
              }}
              error={!!errors.userName}
              helperText={errors.userName}
              required
              fullWidth
              disabled={updateUserMutation.isPending}
            />

            {/* アクセス可能なアカウント（チェックボックス） */}
            <FormControl component="fieldset" error={!!errors.accountIds}>
              <FormLabel component="legend">アクセス可能なアカウント *</FormLabel>
              <FormGroup>
                {accountsLoading ? (
                  <Typography color="text.secondary">読み込み中...</Typography>
                ) : (
                  accounts.map((account) => (
                    <FormControlLabel
                      key={account.id}
                      control={
                        <Checkbox
                          checked={selectedAccountIds.includes(account.id)}
                          onChange={() => handleAccountToggle(account.id)}
                          disabled={updateUserMutation.isPending}
                        />
                      }
                      label={`${account.name || account.id}${account.code ? ` (${account.code})` : ''}`}
                    />
                  ))
                )}
              </FormGroup>
              {errors.accountIds && (
                <Typography color="error" variant="caption">
                  {errors.accountIds}
                </Typography>
              )}
            </FormControl>

            {/* デフォルトアカウント（ラジオボタン） */}
            <FormControl component="fieldset" error={!!errors.defaultAccountId}>
              <FormLabel component="legend">デフォルトアカウント *</FormLabel>
              <RadioGroup
                value={defaultAccountId}
                onChange={(e) => {
                  setDefaultAccountId(e.target.value);
                  setSellerId('');
                  setComparisonSellerId('');
                  setComparisonSellerGroupId('');
                  if (errors.defaultAccountId) {
                    setErrors({ ...errors, defaultAccountId: '' });
                  }
                }}
              >
                {selectedAccountIds.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    アカウントを選択してください
                  </Typography>
                ) : (
                  accounts
                    .filter((acc) => selectedAccountIds.includes(acc.id))
                    .map((account) => (
                      <FormControlLabel
                        key={account.id}
                        value={account.id}
                        control={<Radio disabled={updateUserMutation.isPending} />}
                        label={`${account.name || account.id}${account.code ? ` (${account.code})` : ''}`}
                      />
                    ))
                )}
              </RadioGroup>
              {errors.defaultAccountId && (
                <Typography color="error" variant="caption">
                  {errors.defaultAccountId}
                </Typography>
              )}
            </FormControl>

            {/* 店舗選択（任意） */}
            {defaultAccountId && (
              <TextField
                select
                label="店舗"
                value={sellerId}
                onChange={(e) => {
                  setSellerId(e.target.value);
                  if (errors.sellerId) {
                    setErrors({ ...errors, sellerId: '' });
                  }
                }}
                error={!!errors.sellerId}
                helperText={errors.sellerId || '店舗を変更する場合のみ選択してください'}
                fullWidth
                disabled={updateUserMutation.isPending || sellersLoading}
                slotProps={{
                  inputLabel: { shrink: true },
                  select: { native: true },
                }}
              >
                <option value="">
                  {sellersLoading
                    ? '読み込み中...'
                    : sellers.length === 0
                      ? '店舗がありません'
                      : '変更しない'}
                </option>
                <SellerTreeOptionList options={sellerTreeOptions} />
              </TextField>
            )}

            {/* 比較店舗/比較店舗グループ選択 */}
            {defaultAccountId && (
              <>
                <FormControl component="fieldset">
                  <FormLabel component="legend">比較店舗の指定方法 *</FormLabel>
                  {appDbSettingsLoading ? (
                    <Typography color="text.secondary" variant="body2">
                      読み込み中...
                    </Typography>
                  ) : (
                    <RadioGroup
                      row
                      value={comparisonType}
                      onChange={(e) => {
                        const value = e.target.value as 'seller' | 'seller_group';
                        setComparisonType(value);
                        setComparisonSellerId('');
                        setComparisonSellerGroupId('');
                        setErrors({
                          ...errors,
                          comparisonSeller: '',
                          comparisonSellerGroup: '',
                        });
                      }}
                    >
                      <FormControlLabel
                        value="seller"
                        control={<Radio disabled={updateUserMutation.isPending} />}
                        label="比較店舗で指定"
                      />
                      <FormControlLabel
                        value="seller_group"
                        control={<Radio disabled={updateUserMutation.isPending} />}
                        label="比較店舗グループで指定"
                      />
                    </RadioGroup>
                  )}
                </FormControl>

                {comparisonType === 'seller' && (
                  <TextField
                    select
                    label="比較店舗"
                    value={comparisonSellerId}
                    onChange={(e) => {
                      setComparisonSellerId(e.target.value);
                      if (errors.comparisonSeller) {
                        setErrors({ ...errors, comparisonSeller: '' });
                      }
                    }}
                    error={!!errors.comparisonSeller}
                    helperText={errors.comparisonSeller}
                    required
                    fullWidth
                    disabled={
                      updateUserMutation.isPending || sellersLoading || appDbSettingsLoading
                    }
                    slotProps={{
                      inputLabel: { shrink: true },
                      select: { native: true },
                    }}
                  >
                    <option value="">
                      {sellersLoading
                        ? '読み込み中...'
                        : sellers.length === 0
                          ? '店舗がありません'
                          : '選択してください'}
                    </option>
                    <SellerTreeOptionList options={sellerTreeOptions} />
                  </TextField>
                )}

                {comparisonType === 'seller_group' && (
                  <TextField
                    select
                    label="比較店舗グループ"
                    value={comparisonSellerGroupId}
                    onChange={(e) => {
                      setComparisonSellerGroupId(e.target.value);
                      if (errors.comparisonSellerGroup) {
                        setErrors({ ...errors, comparisonSellerGroup: '' });
                      }
                    }}
                    error={!!errors.comparisonSellerGroup}
                    helperText={errors.comparisonSellerGroup}
                    required
                    fullWidth
                    disabled={
                      updateUserMutation.isPending || sellerGroupsLoading || appDbSettingsLoading
                    }
                    slotProps={{
                      inputLabel: { shrink: true },
                      select: { native: true },
                    }}
                  >
                    <option value="">
                      {sellerGroupsLoading
                        ? '読み込み中...'
                        : sellerGroups.length === 0
                          ? '店舗グループがありません'
                          : '選択してください'}
                    </option>
                    {sellerGroupOptions.map(({ group, depth }) => (
                      <option key={group.id} value={group.id}>
                        {indentSellerGroupLabel(depth, group.name || group.id)}
                      </option>
                    ))}
                  </TextField>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateUserMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleUpdateClick}
            variant="contained"
            disabled={
              updateUserMutation.isPending ||
              accountsLoading ||
              !userName.trim() ||
              selectedAccountIds.length === 0 ||
              !defaultAccountId ||
              (comparisonType === 'seller' && !comparisonSellerId) ||
              (comparisonType === 'seller_group' && !comparisonSellerGroupId)
            }
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400,
          },
        }}
      >
        <DialogTitle>ユーザー情報を更新しますか？</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography>以下の内容でユーザー情報を更新します。</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                <strong>メールアドレス:</strong> {user.user_email}
              </Typography>
              <Typography>
                <strong>ユーザー名:</strong> {userName}
              </Typography>
              <Box>
                <Typography>
                  <strong>アクセス可能なアカウント:</strong>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, ml: 2 }}>
                  {selectedAccountIds.map((id) => (
                    <Chip key={id} label={getAccountDisplayName(id)} size="small" />
                  ))}
                </Box>
              </Box>
              <Typography>
                <strong>デフォルトアカウント:</strong> {getAccountDisplayName(defaultAccountId)}
              </Typography>
              {sellerId && (
                <Typography>
                  <strong>店舗:</strong> {selectedSeller?.name || sellerId}
                </Typography>
              )}
              {comparisonType === 'seller' && (
                <Typography>
                  <strong>比較店舗:</strong> {selectedComparisonSeller?.name || comparisonSellerId}
                </Typography>
              )}
              {comparisonType === 'seller_group' && (
                <Typography>
                  <strong>比較店舗グループ:</strong>{' '}
                  {selectedComparisonSellerGroup?.name || comparisonSellerGroupId}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={updateUserMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            disabled={updateUserMutation.isPending}
          >
            {updateUserMutation.isPending ? '更新中...' : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
